/* import __COLOCATED_TEMPLATE__ from './header.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import type Conversation from 'embercom/objects/inbox/conversation';
import { ConversationState } from 'embercom/objects/inbox/conversation';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type Session from 'embercom/services/session';
import type InboxState from 'embercom/services/inbox-state';
import { ComposerLocation } from 'embercom/services/inbox-state';
import { type Hotkey } from 'embercom/services/inbox-hotkeys';
import type InboxHotkeys from 'embercom/services/inbox-hotkeys';
import { HotkeyID } from 'embercom/services/inbox-hotkeys/HotkeyID';
import type User from 'embercom/objects/inbox/user';
import type InboxApi from 'embercom/services/inbox-api';
import type Router from '@ember/routing/router-service';
import type CtiService from 'embercom/services/cti-service';
import type UserApi from 'embercom/services/user-api';
import { type TableEntryHighlights } from 'embercom/objects/inbox/conversation-table-entry';
import type LinkedTicketsResource from '../linked-tickets-resource';

interface Args {
  conversation: Conversation;
  isLoading?: boolean; // This is true when creating a new conversation
  user: User;
  hideConversationEvents: boolean;
  onToggleConversationEvents: () => unknown;
  openConversation: () => unknown;
  closeConversation: () => unknown;
  snoozeConversation: () => unknown;
  exportConversation: () => void;
  exportConversationPdf?: () => void;
  togglePriority: (conversation: Conversation, priority: boolean) => unknown;
  allowManageParticipants?: boolean;
  highlights?: TableEntryHighlights;
  showIconsOnly?: boolean;
  setTicketTypeToConvertToTicket: (ticketTypeId?: number) => void;
  changeTicketState: InboxState['changeTicketStateLocalUpdates'];
  linkedTicketsResource: LinkedTicketsResource;
}

interface Signature {
  Args: Args;
}

export default class Inbox2ConversationStreamHeaderComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: Session;
  @service declare inboxState: InboxState;
  @service declare intercomEventService: any;
  @service declare inboxApi: InboxApi;
  @service declare router: Router;
  @service declare inboxHotkeys: InboxHotkeys;
  @service declare ctiService: CtiService;
  @service declare userApi: UserApi;
  @tracked conversationId?: number;
  @tracked elementWidth = 0;

  conversationState = ConversationState;
  readonly toggleEventKey: Hotkey;
  readonly handleNotEditorHotkey;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    this.toggleEventKey = this.inboxHotkeys.hotkeysMap[HotkeyID.ToggleEvents];
    this.handleNotEditorHotkey = this.inboxHotkeys.handleNotEditorHotkey;
    if (!this.args.isLoading) {
      this.setConversationId();
    }
  }

  get showIconsOnly() {
    return this.elementWidth < 720;
  }

  get isConversationListHidden() {
    return this.inboxState.isConversationListHidden && this.inboxState.isListView;
  }

  @action onConversationChange() {
    this.setConversationId();
  }

  setConversationId() {
    this.conversationId = this.args.conversation.id;
  }

  @action handleToggleEventsKey(e: KeyboardEvent) {
    if (this.inboxState.activeComposerLocation !== ComposerLocation.ConversationPreviewPanel) {
      this.args.onToggleConversationEvents();
      this.intercomEventService.trackAnalyticsEvent({
        object: 'visibility',
        action: 'changed',
        section: 'conversation_header',
        conversation_id: this.args.conversation.id,
        visibility_type: 'events',
        is_visible: !this.args.hideConversationEvents,
        shortcut_key: true,
      });
      e.preventDefault();
    }
  }

  @action onElementResize(element: HTMLElement) {
    let width = element.getBoundingClientRect().width;
    this.elementWidth = width;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::ConversationPage::Header': typeof Inbox2ConversationStreamHeaderComponent;
    'inbox2/conversation-page/header': typeof Inbox2ConversationStreamHeaderComponent;
  }
}
